<template>
  <v-container fluid>
    <h2>{{ $tc('create_customer') }}</h2>
    <div v-if="user.can('create_customer')">
      <v-form id="customer-create-form" @submit.prevent="onSubmit">
        <v-row>
          <v-col>
            <v-text-field
              v-model="customer.name"
              :label="$tc('name')"
              :error-messages="nameErrors"/>
            <v-select
              v-model="selectedParentCustomer"
              :label="$tc('parent')"
              :items="parentOptions"
              item-value="id"
              item-text="name"
              clearable
              return-object/>
            <v-select
              v-if="selectedParentCustomer"
              v-model="selectedCopyFromCustomer"
              :label="$t('copy_settings_from')"
              :items="childCustomerOptions"
              item-value="id"
              item-text="name"
              clearable
              return-object
              :hint="$t('optional_select_sibling_to_copy_settings')"
              persistent-hint/>

            <v-alert
              v-if="selectedCopyFromCustomer && settingsCount > 0"
              type="info"
              outlined
              class="mt-4">
              {{ settingsCount }} {{ $tc('setting', settingsCount) }} will be copied from
              <strong>{{ selectedCopyFromCustomer.name }}</strong> to the new customer.
            </v-alert>

            <v-btn
              type="submit"
              color="primary"
              class="mt-4"
              @click="$gtag.event('create', {event_category: 'Customer'})"
              :loading="isSaving">
              <v-icon class="ml-n2">mdi-content-save</v-icon>
              {{ $t('create_customer') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </div>
  </v-container>
</template>

<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'
import { required } from 'vuelidate/lib/validators'
import { find, filter } from 'lodash'
import ValidationMixin from '@/components/mixins/ValidationMixin'

export default {
  name: 'CustomerCreate',
  components: {
  },
  mixins: [
    CurrentUserMixin,
    ApiUtilitiesMixin,
    ValidationMixin
  ],
  validations: {
    customer: {
      name: {
        required
      }
    }
  },
  data () {
    return {
      isSaving: false,
      isLoadingSettings: false,
      customer: this.$orm('Customer').createRecord({
        parent_id: null
      }),
      selectedCopyFromCustomer: null,
      rawSettings: []
    }
  },
  watch: {
    selectedParentCustomer (newParent) {
      // Reset copy-from selection when parent changes
      this.selectedCopyFromCustomer = null
      this.rawSettings = []
    },
    selectedCopyFromCustomer (newCustomer) {
      this.rawSettings = []

      if (newCustomer) {
        this.loadCustomerSettings(newCustomer.id)
      }
    }
  },
  computed: {
    /**
     * Computed value to act as a proxy for parent id selection
     */
    selectedParentCustomer: {
      get: function () {
        return find(this.customers, c => c.id === this.customer.parent_id)
      },
      set: function (customer) {
        if (customer == null) {
          this.customer.parent_id = null
        } else {
          this.customer.parent_id = customer.id
        }
      }
    },
    parentOptions () {
      return filter(this.customers, c => c.parent_id === null)
    },
    childCustomerOptions () {
      if (!this.selectedParentCustomer) {
        return []
      }
      return filter(this.customers, c => c.parent_id === this.selectedParentCustomer.id)
    },
    nameErrors () {
      return this.validationErrors([{
        prop: this.$v.customer.name,
        rule: required,
        message: this.$t('required')
      }])
    },
    settingsCount () {
      return this.rawSettings.length
    }
  },
  methods: {
    /**
     * Load customer settings from the selected customer
     */
    loadCustomerSettings (customerId) {
      this.isLoadingSettings = true

      this.$orm('Customer')
        .find(customerId, {
          params: {
            expand: 'customer_settings'
          }
        })
        .then(customer => {
          if (customer && customer.customer_settings) {
            // Fields to exclude from copying
            const excludedFields = ['enterpriseOnboardingComplete', 'onboardingComplete', 'routePlanRuleCount']

            // Store filtered settings for copying
            this.rawSettings = customer.customer_settings.filter(s => !excludedFields.includes(s.key))
          }
        })
        .catch(error => {
          console.error('Failed to load customer settings:', error)
          this.$store.dispatch(
            'globalMessagingModule/showErrorFlashBanner',
            this.$t('failed_to_load_customer_settings')
          )
        })
        .finally(() => {
          this.isLoadingSettings = false
        })
    },

    /**
     * Submit the form to the API.
     */
    onSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) return false

      this.isSaving = true

      // Prepare customer data with settings if any
      const customerData = {
        name: this.customer.name,
        parent_id: this.customer.parent_id
      }

      // Add settings to payload if they exist
      if (this.rawSettings.length > 0) {
        const settingsObj = {}
        this.rawSettings.forEach(setting => {
          settingsObj[setting.key] = setting.value
        })
        customerData.customer_settings = settingsObj
      }

      this.$orm('Customer').createOrUpdate(customerData)
        .then(resp => {
          // Update the current user's list of customers to include the new customer by reloading the user.
          this.$store.dispatch('userModule/socket_userUpdated')
          this.$router.push('/customer/' + resp.id, () => {
            this.$store.dispatch(
              'globalMessagingModule/showSuccessFlashBanner',
              `${this.$i18n.tc('customer')} ${this.$i18n.t('generic_save_success')}`
            )
          })
        })
        .catch((error) => {
          if (error) {
            this.renderRecordSaveErrorFlashBanner(error)
            throw (error)
          }
        })
        .finally(() => {
          this.isSaving = false
        })
    }
  }
}
</script>

<style scoped>
</style>
